import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, BookOpen, Loader2, CheckCircle2, Clock, X } from "lucide-react";

const emptyForm = { title: "", description: "", sections: [{ heading: "", body: "" }] };

export default function AdminModules() {
  const [modules, setModules] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // module id or null (create)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/modules").then(({ data }) => setModules(data)).catch(() => setModules([]));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = async (m) => {
    const { data } = await api.get(`/modules/${m.id}`);
    setEditing(m.id);
    setForm({
      title: data.title,
      description: data.description,
      sections: data.sections.length ? data.sections : [{ heading: "", body: "" }],
    });
    setOpen(true);
  };

  const updateSection = (i, key, val) =>
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));

  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { heading: "", body: "" }] }));
  const removeSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const save = async () => {
    const sections = form.sections.filter((s) => s.heading.trim() && s.body.trim());
    if (!form.title.trim() || sections.length === 0) {
      toast.error("Add a title and at least one section with a heading and body.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/modules/${editing}`, { ...form, sections });
        toast.success("Module updated — quiz is regenerating from the new content");
      } else {
        await api.post("/admin/modules", { ...form, sections });
        toast.success("Module added — quiz is generating in the background");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/modules/${id}`);
      toast.success("Module deleted");
      load();
    } catch {
      toast.error("Could not delete module");
    }
  };

  if (modules === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">Training modules</h1>
        </div>
        <Button data-testid="add-module-button" className="rounded-xl hover:bg-primary/90 active:scale-[0.98]" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" /> Add module
        </Button>
      </div>

      <div className="grid gap-3">
        {modules.map((m) => (
          <div
            key={m.id}
            data-testid={`module-admin-row-${m.id}`}
            className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(37,75,54,0.06)]"
          >
            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
              <BookOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">{m.title}</h3>
              <p className="text-sm text-muted-foreground">{m.description}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <span className="text-muted-foreground">{m.sections.length} sections</span>
                {m.has_quiz ? (
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Quiz ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-semibold text-[hsl(30_70%_38%)]">
                    <Clock className="h-3.5 w-3.5" /> Quiz generating…
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                data-testid={`edit-module-${m.id}`}
                variant="ghost"
                size="icon"
                className="rounded-full text-muted-foreground hover:text-primary"
                onClick={() => openEdit(m)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid={`delete-module-${m.id}`}
                    variant="ghost"
                    size="icon"
                    className="rounded-full text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading">Delete "{m.title}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This removes the module and all attempts for it. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`confirm-delete-module-${m.id}`}
                      className="rounded-xl bg-destructive hover:bg-destructive/90"
                      onClick={() => remove(m.id)}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit module" : "New training module"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input
                data-testid="module-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Manual Handling"
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Short description</Label>
              <Input
                data-testid="module-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="One line summary shown on the card"
                className="h-11 rounded-xl"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Document sections</Label>
                <span className="text-xs text-muted-foreground">The quiz is generated from this content.</span>
              </div>
              {form.sections.map((s, i) => (
                <div key={i} className="rounded-xl border border-border bg-background p-3" data-testid={`section-editor-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`section-heading-${i}`}
                      value={s.heading}
                      onChange={(e) => updateSection(i, "heading", e.target.value)}
                      placeholder={`Section ${i + 1} heading`}
                      className="h-10 rounded-lg"
                    />
                    {form.sections.length > 1 && (
                      <Button
                        data-testid={`remove-section-${i}`}
                        variant="ghost"
                        size="icon"
                        className="shrink-0 rounded-full text-muted-foreground hover:text-destructive"
                        onClick={() => removeSection(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid={`section-body-${i}`}
                    value={s.body}
                    onChange={(e) => updateSection(i, "body", e.target.value)}
                    placeholder="Paste the training text for this section…"
                    className="mt-2 min-h-[100px] rounded-lg"
                  />
                </div>
              ))}
              <Button
                data-testid="add-section-button"
                variant="secondary"
                className="w-full rounded-xl"
                onClick={addSection}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add section
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-module-button"
              className="w-full rounded-xl hover:bg-primary/90"
              disabled={saving}
              onClick={save}
            >
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Save changes" : "Create module"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
